import { z } from "zod";

import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Tenancy } from "@/src/lib/tenancy";
import { USState } from "@/src/lib/us-states";

export const groupSchema = z.object({
  customerId: z.string(),
  id: z.string(),
  name: z.string(),
  tenancy: z.nativeEnum(Tenancy),
  stytchOrgId: z.string(),
  subdomain: z.string(),
  logo: z.string().nullable(),
  timezone: z.string(),
  incomingEmails: z.array(z.string()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
});

export type Group = z.infer<typeof groupSchema>;

const displayNames: Partial<Record<ConfigSubdomain, string>> = {
  [ConfigSubdomain.AUTONOMY]: "Texas Neurology",
  [ConfigSubdomain.ENT_PARTNERS]: "Chicago ENT",
  [ConfigSubdomain.SPORTORTHO]: "Sport Ortho Urgent Care",
};

/* Which states each group operates in. Used to narrow down NPI registry and geocoding search results. */
export const groupStateMapping: Record<string, USState[]> = {
  [ConfigSubdomain.AUTONOMY]: [USState.TX],
  [ConfigSubdomain.COUG]: [USState.OH],
  [ConfigSubdomain.ENT_PARTNERS]: [USState.IL],
  [ConfigSubdomain.ENT_PARTNERS_MENT]: [USState.IL],
  [ConfigSubdomain.ENT_PARTNERS_SCI]: [USState.IL],
  [ConfigSubdomain.ENT_PARTNERS_SUBOTO]: [USState.IL],
  [ConfigSubdomain.HEARTPLACE]: [USState.TX],
  [ConfigSubdomain.HVC]: [USState.GA],
  [ConfigSubdomain.MASS_ENT]: [USState.MD],
  [ConfigSubdomain.NIMBUS]: [USState.GA],
  [ConfigSubdomain.NIRA]: [USState.TX, USState.FL, USState.UT, USState.CO],
  [ConfigSubdomain.PA_EAST_REGION]: [USState.FL],
  [ConfigSubdomain.PINNACLE]: [USState.NV],
  [ConfigSubdomain.QOROS_BC]: [USState.TX],
  [ConfigSubdomain.QOROS_CFCG]: [USState.FL],
  [ConfigSubdomain.SPORTORTHO]: [USState.TN],
  [ConfigSubdomain.TEXAS_HEART_SOUTH_HEART]: [USState.TX],
  [ConfigSubdomain.USHV]: [USState.KS],
  [ConfigSubdomain.USHV_MKC]: [USState.TX],
  [ConfigSubdomain.USHV_MCA]: [USState.TX],
  [ConfigSubdomain.USHV_FORTWORTH]: [USState.TX],
  [ConfigSubdomain.UPPERLINE]: [USState.FL],
  [ConfigSubdomain.UPPERLINE_GEORGIA]: [USState.GA],
  [ConfigSubdomain.UPPERLINE_INDIANA]: [USState.IN],
  [ConfigSubdomain.UPPERLINE_NORTH_FLORIDA]: [USState.FL],
  [ConfigSubdomain.UPPERLINE_ORLANDO]: [USState.FL],
  [ConfigSubdomain.UPPERLINE_TENNESSEE]: [USState.TN, USState.GA],
  [ConfigSubdomain.UVVC]: [USState.CO, USState.IL, USState.GA, USState.FL, USState.TX],
  [ConfigSubdomain.VALERIE]: [USState.CA],
  [ConfigSubdomain.VPS]: [USState.NJ],
  [ConfigSubdomain.SIENT]: [USState.IN],
  [ConfigSubdomain.FCC]: [USState.CO, USState.TN, USState.TX, USState.AZ, USState.FL],
  [ConfigSubdomain.BALANCE_HEALTH]: [
    USState.UT,
    USState.CA,
    USState.AZ,
    USState.WA,
    USState.IL,
    USState.WI,
    USState.MT,
    USState.WY,
    USState.CO,
  ],
};

export function getOperatingStatesForGroup(group: Pick<Group, "subdomain">): USState[] | undefined {
  return groupStateMapping[getGroupConfigSubdomain(group)];
}

export function getGroupDisplayName(group: Pick<Group, "name" | "subdomain">): string {
  const configSubdomain = getGroupConfigSubdomain(group);
  const override = displayNames[configSubdomain as ConfigSubdomain];

  return override ?? group.name;
}

export const groupShape: Group = {
  id: "",
  customerId: "",
  name: "Valerie Health",
  tenancy: Tenancy.DEV,
  stytchOrgId: "",
  subdomain: "",
  logo: null,
  timezone: "",
  incomingEmails: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};
